import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './BookingConfirmation.css';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { getExpertImage } from '../../utils/expertImages';
import { orderService, userSessionService, appointmentService, authService, paymentService } from '../../services';

export default function BookingConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [expertInfo, setExpertInfo] = useState(null);
  const [selectedTime, setSelectedTime] = useState('11.00 AM');
  const [selectedDate, setSelectedDate] = useState('Today');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);

  useEffect(() => {
    // Debug: Log tất cả data nhận được từ location.state
    console.log('BookingConfirmation - Received location.state:', location.state);
    
    // Get expert info from location state
    const expert = location.state?.expert;
    if (expert) {
      console.log('BookingConfirmation - Setting expert info:', expert);
      setExpertInfo(expert);
    }
    
    // preferred package passed from booking flow
    const pkg = location.state?.selectedPackage;
    if (pkg) {
      console.log('BookingConfirmation - Setting selected package:', pkg);
      // attach package info onto expertInfo for display
      setExpertInfo(prev => ({ ...(prev || {}), selectedPackage: pkg }));
    }

    // Get selected time and date from location state
    if (location.state?.selectedTimeSlot) {
      console.log('BookingConfirmation - Setting selected time slot:', location.state.selectedTimeSlot);
      setSelectedTime(location.state.selectedTimeSlot);
    }
    if (location.state?.selectedDate) {
      console.log('BookingConfirmation - Setting selected date:', location.state.selectedDate);
      setSelectedDate(location.state.selectedDate);
    }
  }, [location.state]);

  const handleConfirmBooking = () => {
    if (!termsChecked) {
      alert('Vui lòng đồng ý với các điều khoản và điều kiện trước khi xác nhận.');
      return;
    }
    // perform backend booking flow: create Order (if package) -> create UserSession -> create Appointment
    const performBooking = async () => {
      const currentUser = authService.getCurrentUser();
      if (!currentUser?.id) {
        alert('Please login to confirm booking');
        return;
      }

      try {
        // Determine payment requirement: use package price if present, otherwise expert price
        const pkg = expertInfo?.selectedPackage;
        const sessionRaw = pkg
          ? (pkg.total_price ?? pkg.totalPrice ?? pkg.TotalPrice ?? pkg.pricePerSession ?? pkg.price ?? pkg.amount ?? pkg.total)
          : (expertInfo.price ?? expertInfo.total_price ?? expertInfo.totalPrice ?? expertInfo.pricePerSession ?? null);
        const sessionFeeVal = parseAmount(sessionRaw);

        // If there's a package with a price field present, prefer the payment flow.
        const pkgHasPriceField = !!pkg && (pkg.total_price != null || pkg.totalPrice != null || pkg.TotalPrice != null || pkg.price != null || pkg.pricePerSession != null || pkg.amount != null);

        // Use payment flow when package exists (and likely requires payment) or when sessionFeeVal > 0
        if (pkgHasPriceField || (!isNaN(sessionFeeVal) && Number(sessionFeeVal) > 0)) {
          // Start payment flow: call backend to create order + payment link
          // Use frontend return/cancel URLs so user returns here after payment
          const returnUrl = `${window.location.origin}/payment-result`;
          const cancelUrl = `${window.location.origin}/booking-dashboard`;

          // Derive a friendly buyer name from available user fields
          const buyerName = currentUser?.name || currentUser?.fullName || currentUser?.username || currentUser?.displayName || (currentUser?.email ? String(currentUser.email).split('@')[0] : '');

          const purchasePayload = {
            TherapyPackageId: pkg?.id || pkg?.Id || null,
            BuyerName: buyerName,
            BuyerEmail: currentUser.email || '',
            BuyerPhone: currentUser.phone || '',
            BuyerAddress: currentUser.address || '',
            ReturnUrl: `${returnUrl}?orderId={{orderId}}`,
            CancelUrl: cancelUrl
          };

          // Persist minimal booking context so the frontend can resume after payment redirect
          try {
            const scheduledForValue = location.state?.scheduledFor || null;
            if (scheduledForValue) localStorage.setItem('pendingScheduledFor', scheduledForValue);
            if (expertInfo?.id || expertInfo?._id) localStorage.setItem('pendingTherapistId', expertInfo.id || expertInfo._id);
          } catch (e) { /* ignore storage errors */ }

          // Call payment API to create order and get checkout URL
          // Use camelCase keys to match the Swagger example (backend handles camelCase)
          // and avoid sending a localhost return URL. Omitting or setting returnUrl to null
          // lets the backend use a safe HTTPS default.
          // Build a richer description (nội dung) for the payment so it appears clearly in PayOS
          const therapistName = expertInfo?.displayName || expertInfo?.name || expertInfo?.username || '';
          const packageTitle = pkg?.title || pkg?.name || pkg?.displayName || '';
          const scheduled = location.state?.scheduledFor || `${selectedDate} ${selectedTime}`;
          const descriptionParts = [];
          if (packageTitle) descriptionParts.push(`Gói: ${packageTitle}`);
          if (therapistName) descriptionParts.push(`Chuyên gia: ${therapistName}`);
          if (scheduled) descriptionParts.push(`Lịch: ${scheduled}`);
          // Include session count or amount when available
          const sessionCount = pkg?.sessions || pkg?.numberOfSessions || pkg?.sessionCount;
          if (sessionCount) descriptionParts.push(`Số buổi: ${sessionCount}`);

          const description = descriptionParts.join(' | ') || `Payment for ${pkg ? 'therapy package' : 'session'}`;

          // Include platform fee in the payment amount so PayOS receives the full charge
          const platformFeeVal = 50000; // hard-coded platform fee shown in UI
          const totalVal = (isNaN(sessionFeeVal) ? 0 : sessionFeeVal) + platformFeeVal;

          const camelPayload = {
            therapyPackageId: purchasePayload.TherapyPackageId,
            buyerName: purchasePayload.BuyerName,
            buyerEmail: purchasePayload.BuyerEmail,
            buyerPhone: purchasePayload.BuyerPhone,
            buyerAddress: purchasePayload.BuyerAddress,
            description: description,
            amount: Math.round(Number(totalVal) || 0),
            returnUrl: null,
            cancelUrl: purchasePayload.CancelUrl,
            therapistId: expertInfo?.id || expertInfo?._id || null,
            scheduledFor: scheduled
          };

          const payRes = await paymentService.purchaseTherapyPackage(camelPayload);

          // Debug: log the full response returned by the payment creation API
          console.log('Payment creation response:', payRes);

          // Backend may return the payload either wrapped under `data` or at top-level.
          // Attempt to find OrderId and CheckoutUrl in multiple possible shapes.
          const checkout = payRes?.data?.checkoutUrl || payRes?.checkoutUrl || payRes?.CheckoutUrl || (payRes?.data && (payRes.data.checkoutUrl || payRes.data.CheckoutUrl));
          const orderId = payRes?.data?.orderId || payRes?.orderId || payRes?.OrderId || (payRes?.data && (payRes.data.orderId || payRes.data.OrderId));

          // Persist order id so the return page can pick it up (in case PayOS doesn't preserve our query params)
          if (orderId) {
            try { localStorage.setItem('pendingOrderId', orderId); } catch (e) { }
          }

          if (checkout) {
            // Redirect to PayOS checkout to complete payment. PayOS will redirect back to the ReturnUrl provided.
            window.location.href = checkout;
            return;
          }

          throw new Error('Payment creation failed: missing checkout URL');
        } else {
          // No payment required -> existing immediate booking flow (create session + appointment)
          let orderId = null;
          if (pkg && (pkg.id || pkg.Id)) {
            const amount = sessionRaw || 0;
            // Send snake_case payload to match backend naming policy
            const orderPayload = {
              user_id: currentUser.id,
              therapy_package_id: pkg.id || pkg.Id,
              amount_paid: amount,
              payment_status_value: 0,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };

            // Debug: log payload to inspect what is being sent to backend
            console.log('Order payload about to be sent:', orderPayload);

            // Basic client-side validation: ensure IDs look like GUIDs to avoid server errors
            const isGuid = (s) => {
              if (!s) return false;
              const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
              return guidRegex.test(String(s));
            };

            if (!isGuid(orderPayload.UserId) || !isGuid(orderPayload.TherapyPackageId)) {
              console.error('Invalid GUID detected in order payload, aborting create:', orderPayload);
              alert('Booking failed: invalid user or package identifier. Please refresh and try again.');
              return;
            }

            const orderRes = await orderService.create(orderPayload);
            orderId = orderRes?.id || orderRes?.Id || orderRes?.Id;
          }

          // create UserSession
          // Create session with snake_case fields
          const sessionPayload = {
            user_id: currentUser.id,
            status_value: 0,
            created_at: new Date().toISOString(),
            expires_at: new Date(Date.now() + 1000 * 60 * 60).toISOString()
          };
          if (orderId) sessionPayload.order_id = orderId;
          sessionPayload.user = { id: currentUser.id };
          const sessionRes = await userSessionService.createUserSession(sessionPayload);
          const userSessionId = sessionRes?.id || sessionRes?.Id || sessionRes?.Id;

          // create Appointment
          const scheduledForValue = location.state?.scheduledFor || null;
          // Create appointment with snake_case fields
          const appointmentPayload = {
            therapist_id: expertInfo.id || expertInfo._id,
            client_id: currentUser.id,
            user_session_id: userSessionId,
            scheduled_for: scheduledForValue,
            duration_minutes: expertInfo.durationMinutes || 60,
            status_value: 0,
            meeting_url: null,
            created_at: new Date().toISOString()
          };
          if (pkg && (pkg.id || pkg.Id)) appointmentPayload.therapy_package_id = pkg.id || pkg.Id;
          const apptRes = await appointmentService.createAppointment(appointmentPayload);

          // success
          setIsConfirmed(true);
        }
      } catch (err) {
        console.error('Booking confirmation failed', err);
        if (err?.response) {
          alert(`Booking failed: ${JSON.stringify(err.response.data)}`);
        } else {
          alert('Booking failed. Please try again.');
        }
      }
    };

    performBooking();
  };

  const handleGoToCalendar = () => {
    navigate('/appointment-calendar', { 
      state: { 
        message: 'Booking confirmed successfully!',
        expert: expertInfo,
        selectedDate: selectedDate,
        selectedTimeSlot: selectedTime
      }
    });
  };

  const handleCancel = () => {
    navigate('/booking-dashboard');
  };

  const parseAmount = (value) => {
    if (value === null || value === undefined) return NaN;
    if (typeof value === 'number') return value;
    // Strip non-digit characters (thousands separators, currency symbols)
    const digits = String(value).replace(/[^0-9\-\.]/g, '');
    const n = Number(digits);
    return isNaN(n) ? NaN : n;
  };

  const formatVnd = (amount) => {
    if (amount === null || amount === undefined || isNaN(Number(amount))) return '—';
    try {
      const n = Number(amount);
      return n.toLocaleString('vi-VN') + 'đ';
    } catch (e) {
      return String(amount);
    }
  };

  if (!expertInfo) {
    return (
      <div className="booking-confirmation page-with-header">
        <Header />
        <div className="appointment-layout">
          <div className="booking-main-content">
            <div className="confirmation-container">
              <div className="confirmation-header">
                <h1>Chưa chọn chuyên gia</h1>
                <p>Vui lòng quay lại để chọn chuyên gia trước khi đặt lịch</p>
                <button 
                  className="back-to-booking-btn" 
                  onClick={() => navigate('/book-expert')}
                >
                  Chọn chuyên gia
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="booking-confirmation page-with-header">
      <Header />
      
      <div className="appointment-layout">

        {/* Main Content - Booking Confirmation */}
        <div className="booking-main-content">
          <div className="confirmation-container">
            {!isConfirmed ? (
              <>
                {/* Header */}
                <div className="confirmation-header">
                  <h1>Xác nhận đặt lịch của bạn</h1>
                  <p>Vui lòng xem lại chi tiết cuộc hẹn trước khi xác nhận</p>
                </div>

                {/* Expert Information */}
                <div className="expert-info-card">
                  <div className="expert-avatar">
                    <img
                      src={getExpertImage(expertInfo)}
                      alt={expertInfo.name}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `data:image/svg+xml;base64,${btoa(`<svg width="120" height="120" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg"><rect width="120" height="120" fill="#6366f1"/><text x="60" y="72" font-family="Arial, Helvetica, sans-serif" font-size="48" fill="#fff" text-anchor="middle">${(expertInfo?.name || 'U').charAt(0)}</text></svg>`)}`;
                      }}
                    />
                  </div>
                  <div className="expert-details">
                    <h2>{expertInfo.name}</h2>
                    <p className="expert-specialty">{expertInfo.specialty}</p>
                    <div className="expert-description">
                      <p>Chuyên gia tâm lý học lâm sàng với nhiều năm kinh nghiệm trong điều trị các rối loạn tâm lý và tư vấn tâm lý.</p>
                    </div>
                    <div className="expert-stats">
                      <span className="experience">{expertInfo.experience} kinh nghiệm</span>
                      <span className="rating">⭐ {expertInfo.rating}</span>
                    </div>
                  </div>
                  <div className="expert-price">
                    <span className="price-label">Phí buổi tư vấn</span>
                    <span className="price-amount">{expertInfo.price}</span>
                  </div>
                </div>

                {/* Appointment Details */}
                <div className="appointment-details">
                  <h3>Chi tiết cuộc hẹn</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="detail-label">Ngày</span>
                      <span className="detail-value">{selectedDate}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Thời gian</span>
                      <span className="detail-value">{selectedTime}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Thời lượng</span>
                      <span className="detail-value">60 phút</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Loại phiên</span>
                      <span className="detail-value">Cuộc gọi video trực tuyến</span>
                    </div>
                  </div>
                </div>

                {/* Payment Summary */}
                <div className="payment-summary">
                  <h3>Tóm tắt thanh toán</h3>
                  <div className="payment-details">
                    {/** compute session fee from expertInfo.price (fallbacks kept simple) */}
                    {(() => {
                      // Prefer selected package price when present
                      const pkg = expertInfo?.selectedPackage;
                      const sessionSource = pkg ? 'package' : 'expert';
                      const sessionLabel = pkg ? (pkg.name || pkg.packageName || 'Gói') : 'Phí buổi tư vấn';
                      const sessionRaw = pkg ? (pkg.totalPrice || pkg.TotalPrice || pkg.pricePerSession || pkg.price) : (expertInfo.price || expertInfo.totalPrice || expertInfo.pricePerSession);
                      const sessionFeeVal = parseAmount(sessionRaw);
                      const platformFeeVal = 50000; // fixed platform fee
                      const totalVal = (isNaN(sessionFeeVal) ? 0 : sessionFeeVal) + platformFeeVal;
                      return (
                        <>
                          <div className="payment-item">
                            <span>{sessionLabel}</span>
                            <span>{isNaN(sessionFeeVal) ? (sessionRaw || '—') : formatVnd(sessionFeeVal)}</span>
                          </div>
                          <div className="payment-item">
                            <span>Phí nền tảng</span>
                            <span>{formatVnd(platformFeeVal)}</span>
                          </div>
                          <div className="payment-item total">
                            <span>Tổng số tiền</span>
                            <span>{formatVnd(totalVal)}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Terms and Conditions */}
                <div className="terms-section">
                  <h3>Điều khoản và điều kiện</h3>
                  <div className="terms-content">
                    <p>• Phiên có thể được dời lịch tối đa 24 giờ trước cuộc hẹn</p>
                    <p>• Hoàn tiền đầy đủ nếu hủy trước 48 giờ</p>
                    <p>• Liên kết cuộc gọi video sẽ được gửi 30 phút trước phiên</p>
                    <p>• Vui lòng đảm bảo kết nối internet ổn định cho phiên</p>
                  </div>
                  <div className="terms-checkbox">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={termsChecked}
                      onChange={(e) => setTermsChecked(e.target.checked)}
                    />
                    <label htmlFor="terms">Tôi đồng ý với các điều khoản và điều kiện</label>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="confirmation-actions">
                  <button className="cancel-btn" onClick={handleCancel}>
                    Hủy
                  </button>
                  <button
                    className={`confirm-btn ${!termsChecked ? 'disabled' : ''}`}
                    onClick={handleConfirmBooking}
                    disabled={!termsChecked}
                    aria-disabled={!termsChecked}
                  >
                    Xác nhận đặt lịch
                  </button>
                </div>
              </>
            ) : (
              /* Confirmation Success */
              <div className="confirmation-success">
                <h1>Đặt lịch đã được xác nhận!</h1>
                <p>Cuộc hẹn của bạn đã được lên lịch thành công</p>
                <div className="success-details">
                  <div className="success-item">
                    <span className="success-label">Chuyên gia:</span>
                    <span className="success-value">{expertInfo.name}</span>
                  </div>
                  <div className="success-item">
                    <span className="success-label">Ngày & Giờ:</span>
                    <span className="success-value">{selectedDate} at {selectedTime}</span>
                  </div>
                  <div className="success-item">
                    <span className="success-label">Mã xác nhận:</span>
                    <span className="success-value">#BK{Date.now().toString().slice(-6)}</span>
                  </div>
                </div>
                <div className="success-message">
                  <p>Bạn sẽ nhận được email xác nhận với liên kết cuộc gọi video trong thời gian ngắn.</p>
                  <p>Vui lòng kiểm tra email 30 phút trước phiên.</p>
                </div>
                
                {/* Action Buttons for Success State */}
                <div className="success-actions">
                  <button className="view-calendar-btn" onClick={handleGoToCalendar}>
                    <span>Xem lịch của tôi</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

